import { Menu, X, Zap } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, {
  useContext,
  useMemo,
  useCallback,
  memo,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import LoginButton from 'src/components/LoginButton';
import SignupButton from 'src/components/SignupButton';
import { useAdmin } from 'src/context/AdminContext';
import { useMobileMenu } from 'src/context/MobileMenuContext';
import { SignatureContext } from 'src/context/SignatureContext';
import { useViewport } from 'src/hooks/useViewport';
import { useAccount, useDisconnect } from 'wagmi';

const logHeaderState = (mainHeader: boolean, mobileHeader: boolean) => {
  if (!mainHeader && !mobileHeader) {
    console.warn('No header is currently displayed');
  } else {
    console.log('Header State:', {
      mainHeader,
      mobileHeader,
      mainHeaderElement: document.querySelector('header.main-header'),
      mobileHeaderElement: document.querySelector('div.mobile-header'),
    });
  }
};

// Move MobileMenu outside of Header component
const MobileMenu = React.memo(
  ({
    navigationLinks,
    onClose,
    isOpen,
  }: {
    navigationLinks: Array<{ path: string; label: string }>;
    onClose: () => void;
    isOpen: boolean;
  }) => {
    const pathname = usePathname();
    const { isConnected, address } = useAccount();

    return (
      <div
        className={`mobile-header fixed inset-0 z-[100] bg-gray-900 transition-all duration-300 ease-in-out md:hidden ${isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <div className='flex h-full flex-col bg-gray-900'>
          <div className="flex justify-end p-4">
            <button
              onClick={onClose}
              className='rounded-lg p-2 text-gray-300 transition-colors duration-200 hover:bg-blue-500/5 hover:text-blue-400'
            >
              <X size={24} />
            </button>
          </div>
          <nav className='flex flex-1 flex-col items-center justify-center space-y-6'>
            {navigationLinks.map(({ path, label }) => (
              <Link
                key={path}
                href={path}
                onClick={onClose}
                className={`flex items-center space-x-3 rounded-xl px-6 py-3 text-xl transition-colors duration-200 ${
                  pathname === path
                    ? 'bg-blue-500/10 text-blue-400'
                    : 'text-gray-300 hover:bg-blue-500/5 hover:text-blue-400'
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>
          <div className='flex justify-center p-6'>
            {isConnected && address ? <SignupButton /> : <LoginButton />}
          </div>
        </div>
      </div>
    );
  },
);

MobileMenu.displayName = 'MobileMenu';

const Header: React.FC<{ title: string }> = memo(({ title }) => {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { setSignature } = useContext(SignatureContext);
  const pathname = usePathname();
  const { isMenuOpen, setIsMenuOpen } = useMobileMenu();
  const { isAdmin } = useAdmin();
  const { isMobile, isClient } = useViewport();

  const handleMobileMenuToggle = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen, setIsMenuOpen]);

  const navigationLinks = useMemo(
    () => [
      { path: '/', label: 'Home' },
      { path: '/memes', label: 'Memes' },
      { path: '/social', label: 'Social' },
      { path: '/roblox', label: 'Roblox' },
      // { path: '/merch', label: 'Merch' },
      { path: '/nft', label: 'NFTs' },
      ...(isAdmin ? [{ path: '/admin', label: 'Admin' }] : []),
    ],
    [isAdmin],
  );

  // We only need this one effect for logging
  useEffect(() => {
    const mainHeaderVisible = !!document.querySelector('header.main-header');
    const mobileHeaderVisible = isMenuOpen;
    logHeaderState(mainHeaderVisible, mobileHeaderVisible);
  }, [isMenuOpen]);

  useEffect(() => {
    const desktopNav = document.querySelector('nav.desktop-nav');
    console.log('Desktop Nav Visibility:', {
      element: desktopNav,
      isVisible: desktopNav
        ? window.getComputedStyle(desktopNav).display
        : 'not found',
      classList: desktopNav?.classList,
    });
  }, []);

  useEffect(() => {
    const desktopNav = document.querySelector('nav.desktop-nav');
    const walletContainer = document.querySelector('.wallet-container-desktop');

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const element = mutation.target as HTMLElement;
          const isDesktop = window.matchMedia('(min-width: 768px)').matches;

          if (isDesktop) {
            if (element.classList.contains('hidden')) {
              element.classList.remove('hidden');
            }
          }
        }
      });
    });

    if (desktopNav) {
      observer.observe(desktopNav, { attributes: true });
    }
    if (walletContainer) {
      observer.observe(walletContainer, { attributes: true });
    }

    return () => {
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    const handleResize = () => {
      const desktopNav = document.querySelector('nav.desktop-nav');
      const walletContainer = document.querySelector('div.wallet-container');

      if (window.innerWidth >= 768) {
        desktopNav?.classList.remove('hidden');
        desktopNav?.classList.add('flex');
        walletContainer?.classList.remove('hidden');
        walletContainer?.classList.add('block');
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <header className='main-header relative z-[1000] w-full bg-gray-900 backdrop-blur-sm'>
        <div className='mx-auto max-w-7xl'>
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              {/* Logo and Navigation container */}
              <div className="flex items-center space-x-6">
                <Link href="/" className='group relative flex items-center'>
                  <div className="relative">
                    <div className='absolute inset-0 bg-blue-500 opacity-20 blur-lg transition-all duration-300 group-hover:opacity-30' />
                    <div className='relative rounded-lg bg-blue-600 p-2'>
                      <Zap size={24} className='animate-pulse text-white' />
                    </div>
                  </div>
                </Link>

                {/* Desktop Nav */}
                {isClient && (
                  <nav
                    className={`desktop-nav items-center space-x-1 ${isMobile ? 'hidden' : 'flex'}`}
                  >
                    {navigationLinks.map(({ path, label }) => (
                      <Link
                        key={path}
                        href={path}
                        className={`rounded-lg px-4 py-2 transition-colors duration-200 ${
                            pathname === path
                              ? 'bg-blue-500/10 text-blue-400'
                              : 'text-gray-300 hover:bg-blue-500/5 hover:text-blue-400'
                          }`}
                      >
                        {label}
                      </Link>
                    ))}
                  </nav>
                )}
              </div>

              {/* Auth and Mobile Menu container */}
              <div className="flex items-center space-x-4">
                {/* Desktop Auth */}
                {isClient && (
                  <div
                    className={`wallet-container-desktop ${isMobile ? 'hidden' : 'block'}`}
                  >
                    {isConnected && address ? (
                      <SignupButton />
                    ) : (
                      <LoginButton />
                    )}
                  </div>
                )}

                {/* Mobile Menu Button */}
                {isClient && isMobile && (
                  <button
                    onClick={handleMobileMenuToggle}
                    className={`rounded-lg p-2 transition-colors duration-200 ${
                        isMenuOpen
                          ? 'bg-blue-500/10 text-blue-400'
                          : 'text-gray-300 hover:bg-blue-500/5 hover:text-blue-400'
                      }`}
                  >
                    {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {isClient &&
        createPortal(
          <MobileMenu
            navigationLinks={navigationLinks}
            onClose={() => {
              setIsMenuOpen(false);
              const mainHeaderVisible =
                !!document.querySelector('header.main-header');
              logHeaderState(mainHeaderVisible, false);
            }}
            isOpen={isMenuOpen}
          />,
          document.body,
        )}
    </>
  );
});

Header.displayName = 'Header';

export default Header;
